// src/components/Canvas/Grid/Grid.tsx
import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import gsap from 'gsap';
import { useUiStateStore } from 'src/stores/uiStateStore';
import { useResizeObserver } from 'src/hooks/useResizeObserver';
import { useGrid } from 'src/hooks/useGrid';
import gridTileSvg from 'src/assets/grid-tile-bg.svg';

/**
 * Grid Component - Renders the isometric grid background
 * Uses core IsometricGrid class for all calculations
 * Adapts to dark/light theme
 */
export const Grid = () => {
  const elementRef = useRef<HTMLDivElement>(null);
  const { size } = useResizeObserver(elementRef.current);
  const [isDark, setIsDark] = useState(false);
  const previousValuesRef = useRef<{
    backgroundSizeX: number;
    backgroundSizeY: number;
    backgroundPosX: number;
    backgroundPosY: number;
  } | null>(null);

  // Get grid instance
  const grid = useGrid();

  // Get UI state from store
  const scroll = useUiStateStore((state) => state.scroll);
  const zoom = useUiStateStore((state) => state.zoom);

  // Watch for theme changes
  useEffect(() => {
    const checkTheme = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };

    // Check initial theme
    checkTheme();

    // Watch for class changes on document element
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!elementRef.current) return;

    // Use core grid logic for calculations
    const elSize = elementRef.current.getBoundingClientRect();
    const rendererSize = { width: elSize.width, height: elSize.height };

    const backgroundPos = grid.calculateBackgroundPosition(
      scroll,
      zoom,
      rendererSize
    );

    const newBackgroundSizeX = backgroundPos.tileSize.width;
    const newBackgroundSizeY = backgroundPos.tileSize.height * 2;
    const newBackgroundPosX = backgroundPos.x;
    const newBackgroundPosY = backgroundPos.y;

    // Check if values have actually changed
    const valuesChanged =
      !previousValuesRef.current ||
      previousValuesRef.current.backgroundSizeX !== newBackgroundSizeX ||
      previousValuesRef.current.backgroundSizeY !== newBackgroundSizeY ||
      previousValuesRef.current.backgroundPosX !== newBackgroundPosX ||
      previousValuesRef.current.backgroundPosY !== newBackgroundPosY;

    // Only animate if values have changed
    if (valuesChanged) {
      // Keep the grid locked to the scene during continuous zoom and pan.
      gsap.set(elementRef.current, {
        backgroundSize: `${newBackgroundSizeX}px ${newBackgroundSizeY}px`,
        backgroundPosition: `${newBackgroundPosX}px ${newBackgroundPosY}px`
      });

      // Store the new values
      previousValuesRef.current = {
        backgroundSizeX: newBackgroundSizeX,
        backgroundSizeY: newBackgroundSizeY,
        backgroundPosX: newBackgroundPosX,
        backgroundPosY: newBackgroundPosY
      };
    }

  }, [scroll, zoom, size, grid]);

  return (
    <Box
      data-grid="true"
      sx={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        pointerEvents: 'none'
      }}
    >
      <Box
        ref={elementRef}
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: `repeat url("${gridTileSvg}")`,
          // Invert colors in dark mode to make grid visible
          filter: isDark ? 'invert(1) opacity(0.3)' : 'opacity(1)',
          transition: 'filter 0.3s ease'
        }}
      />
    </Box>
  );
};
